/**
 * @file QuickCaptureFab.tsx
 * @description Global floating "+" button (bottom-right, every page) that opens a small
 * panel to jot a thought and POST it to /api/inbox (session-authed quick capture). This is
 * the single UI entry that makes the whole HERMES capture→inbox→task loop usable without the
 * iOS shortcut. Cmd/Ctrl+Enter submits, Escape closes. Copy is Polish (instance convention).
 * Takes over the slot previously held by the fork's Tabby mascot.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Plus, X, Send, Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export function QuickCaptureFab() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) taRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = useCallback(async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.inbox.capture(t);
      setText("");
      setDone(true);
      window.setTimeout(() => {
        setDone(false);
        setOpen(false);
      }, 900);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [text, busy]);

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[min(92vw,22rem)] card p-3 shadow-2xl animate-slide-up">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-200">Szybka wrzutka → Inbox</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Zamknij"
              className="text-gray-500 hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
            }}
            rows={3}
            placeholder="Co Ci chodzi po głowie? (⌘/Ctrl + Enter = zapisz)"
            className="input w-full resize-none text-sm"
          />
          {error && <p className="mt-1 text-[11px] text-red-400 break-words">{error}</p>}
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={submit}
              disabled={busy || !text.trim()}
              className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : done ? (
                <Check className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {done ? "Dodano" : "Zapisz do Inbox"}
            </button>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Szybka wrzutka"
        title="Szybka wrzutka"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-accent text-white shadow-2xl flex items-center justify-center hover:opacity-90 transition-transform hover:scale-105 active:scale-95"
      >
        {open ? <X className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
      </button>
    </>
  );
}
